import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { ScrollView } from "react-native";
import { generatePath, useNavigate, useParams } from "react-router";
import { Spinner, View } from "@lookiero/aurora";
import { QueryStatus } from "@lookiero/messaging-react";
import { ProductPreviewWithDiscount } from "../../../../domain/projections/ProductPreviewWithDiscount";
import { useViewIsSalesOfferingEnabled } from "../../../projection/offering/react/useViewIsSalesOfferingEnabled";
import { useGetCategories } from "../../behaviors/useGetCategories";
import { useGetExperiments } from "../../behaviors/useGetExperiments";
import { useHardwareBackPress } from "../../behaviors/useHardwareBackPress";
import { useMoreProductsPerCategory } from "../../behaviors/useMoreProductsPerCategory";
import { Categories as CategoriesTemplate } from "../../components/templates/categories/Categories";
import { InViewProvider } from "../../hooks/useInView";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { Routes } from "../../routing/routes";
import { STORAGE_KEY_CATEGORIES_SCROLL } from "../../shared/util/storageKeys";
import { List, TrackingPage, TrackingView } from "../../tracking/tracking";
import { useTrackCategoryItemSelected } from "../../tracking/useTrackCategoryItemSelected";
import { useTrackCategoryTabSelected } from "../../tracking/useTrackCategoryTabSelected";
import { useTrackCategoryViewItemList } from "../../tracking/useTrackCategoryViewItemList";
import { useTrackPageView } from "../../tracking/useTrackPageView";
import { DEFAULT_SCROLL_VALUE } from "../completeYourLook/CompleteYourLook.definition";
import { style } from "./Categories.style";

interface CategoriesProps {
  readonly onBack: () => void;
}

const Categories: FC<CategoriesProps> = ({ onBack }) => {
  const {
    session: { user },
    store,
    basePath,
    tradename,
  } = useStaticInfo();
  const navigate = useNavigate();
  const params = useParams();
  const { categoryId: categoryIdFromRoute = "" } = params;

  const [isSalesOfferingEnabled, isSalesOfferingEnabledStatus] = useViewIsSalesOfferingEnabled();

  const trackingCommonParams = useMemo(
    () => ({
      country: user.country,
      segment: user.segment,
      userId: user.id,
      tradename,
    }),
    [user.country, user.id, tradename, user.segment],
  );
  useTrackPageView({
    ...trackingCommonParams,
    page: TrackingPage.CATEGORIES,
    view: TrackingView.LOOK_IN_CATEGORIES,
  });
  const trackCategoryTabSelected = useTrackCategoryTabSelected(trackingCommonParams);
  const trackCategoryItemSelected = useTrackCategoryItemSelected(trackingCommonParams);
  const trackCategoryViewItemList = useTrackCategoryViewItemList(trackingCommonParams);

  const { experiments } = useGetExperiments();

  const scrollViewRef = useRef<ScrollView | null>(null);
  const scrollValueRef = useRef<number>(DEFAULT_SCROLL_VALUE);

  const saveScrollValueInStore = useCallback(
    async (value: number) => {
      await store.set(STORAGE_KEY_CATEGORIES_SCROLL, value < 0 ? 0 : value);
    },
    [store],
  );

  const moreProductsPerCategory = useMoreProductsPerCategory();

  const { isLoading, categories } = useGetCategories(moreProductsPerCategory);

  const categoryIds = useMemo(() => {
    if (!isLoading && categories) {
      return categories.map((category) => category.id);
    }
    return [];
  }, [categories, isLoading]);

  useEffect(() => {
    if (categoryIdFromRoute) {
      return;
    }

    if (categoryIds) {
      const firstCategoryId = categoryIds[0];

      if (firstCategoryId) {
        navigate(generatePath(`${basePath}/${Routes.CATEGORY}`, { categoryId: firstCategoryId }), { replace: true });
      }
    }
  }, [basePath, categoryIdFromRoute, categoryIds, navigate]);

  const productsPerCategory = useMemo(() => {
    if (!isLoading && categories) {
      const tempProductsPerCategory = {} as Record<string, ProductPreviewWithDiscount[]>;
      Object.values(categories).forEach((category) => {
        tempProductsPerCategory[category.id] = category.products;
      });
      return tempProductsPerCategory;
    }
    return undefined;
  }, [categories, isLoading]);

  const productsToShow = useMemo(() => {
    if (categoryIdFromRoute && productsPerCategory) {
      return productsPerCategory[categoryIdFromRoute];
    }
    return undefined;
  }, [categoryIdFromRoute, productsPerCategory]);

  const handleOnPressCategory = useCallback(
    (categoryId: string) => {
      trackCategoryTabSelected({ tab: categoryId });

      saveScrollValueInStore(DEFAULT_SCROLL_VALUE);

      navigate(generatePath(`${basePath}/${Routes.CATEGORY}`, { categoryId }), { replace: true });
    },
    [trackCategoryTabSelected, saveScrollValueInStore, navigate, basePath],
  );

  const handleOnPressProductPreview = useCallback(
    (product: ProductPreviewWithDiscount, index: number) => {
      trackCategoryItemSelected({
        list: List.CATEGORIES,
        productPreview: product,
        category: categoryIdFromRoute,
        index,
      });

      saveScrollValueInStore(scrollValueRef.current);

      navigate(
        generatePath(`${basePath}/${Routes.PRODUCT_CATEGORY_DETAIL}`, {
          variantId: product.id,
          categoryId: categoryIdFromRoute,
        }),
        { state: { innerNavigation: true } },
      );
    },
    [trackCategoryItemSelected, categoryIdFromRoute, saveScrollValueInStore, navigate, basePath],
  );

  const handleOnScroll = useCallback((coords: { X: number; Y: number }) => {
    scrollValueRef.current = coords.Y;
  }, []);

  const handleOnLayoutProductList = useCallback(async () => {
    const storedScroll = await store.get(STORAGE_KEY_CATEGORIES_SCROLL);
    if (Number(storedScroll) >= 0 && scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ x: 0, y: Number(storedScroll), animated: false });
    }

    if (productsToShow && !isLoading) {
      trackCategoryViewItemList({
        experiments,
        list: List.CATEGORIES,
        categoryId: categoryIdFromRoute,
        products: productsToShow,
      });
    }
  }, [categoryIdFromRoute, experiments, isLoading, productsToShow, store, trackCategoryViewItemList]);

  useHardwareBackPress(onBack);

  const dependenciesLoadedStatuses = [QueryStatus.ERROR, QueryStatus.SUCCESS];
  const dependenciesLoaded = !isLoading && dependenciesLoadedStatuses.includes(isSalesOfferingEnabledStatus);

  if (!dependenciesLoaded) {
    return (
      <View style={style.spinnerWrapper}>
        <Spinner />
      </View>
    );
  }

  return (
    <InViewProvider key="categories" resetKey={categoryIdFromRoute}>
      <CategoriesTemplate
        categoryIds={categoryIds}
        isLoading={isLoading}
        isSalesOfferingEnabled={isSalesOfferingEnabled || false}
        products={productsToShow}
        scrollViewRef={scrollViewRef}
        selectedCategoryId={categoryIdFromRoute}
        onBack={onBack}
        onLayoutProductList={handleOnLayoutProductList}
        onPressCategory={handleOnPressCategory}
        onPressProductPreview={handleOnPressProductPreview}
        onScroll={handleOnScroll}
      />
    </InViewProvider>
  );
};

export { Categories };
